import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * LegalPageShell — Stage 20B+ Wave A.
 *
 * A premium, editorial shell for the public trust surfaces
 * (/privacy · /terms · /security · /faq · /contact). Calm paper
 * background, serif eyebrow+headline, sans body, hairline dividers.
 * No walls of text — readable, reassuring, short. Visual DNA matches
 * the Stage 20A landing page (PAPER/NAVY/TEAL).
 *
 * Usage:
 *   <LegalPageShell eyebrow="Privacy" title="Your data, quietly protected.">
 *     <Section title="...">...</Section>
 *     <Section title="...">...</Section>
 *   </LegalPageShell>
 */

const PAPER = '#FBFAF6';
const NAVY = '#0E1C2E';
const NAVY_MUTED = 'rgba(14, 28, 46, 0.72)';
const NAVY_SOFT = 'rgba(14, 28, 46, 0.55)';
const TEAL = '#2FB3A0';
const HAIRLINE = 'rgba(14, 28, 46, 0.10)';

const BackToHome = () => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate('/')}
      className="inline-flex items-center gap-2 font-sans text-[12.5px] hover:text-[#0E1C2E] transition-colors"
      style={{ color: NAVY_SOFT }}
      data-testid="legal-back-home"
    >
      <span>←</span>
      <span>Back to Kindra</span>
    </button>
  );
};

const TrustNav = ({ current }) => {
  const navigate = useNavigate();
  const links = [
    { href: '/privacy', label: 'Privacy' },
    { href: '/security', label: 'Security' },
    { href: '/terms', label: 'Terms' },
    { href: '/medical-disclaimer', label: 'Medical' },
    { href: '/faq', label: 'FAQ' },
    { href: '/contact', label: 'Contact' },
  ];
  return (
    <nav
      className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-[12.5px]"
      style={{ color: NAVY_SOFT }}
      aria-label="Trust surfaces"
    >
      {links.map((l) => (
        <button
          key={l.href}
          onClick={() => navigate(l.href)}
          className={`transition-colors hover:text-[#0E1C2E] ${current === l.href ? 'text-[#0E1C2E] font-medium' : ''}`}
          data-testid={`legal-nav-${l.label.toLowerCase()}`}
        >
          {l.label}
        </button>
      ))}
    </nav>
  );
};

export const Section = ({ title, eyebrow, children, testid }) => (
  <section className="max-w-[680px] mx-auto pb-10 md:pb-14" data-testid={testid}>
    {eyebrow && (
      <p
        className="font-sans text-[10.5px] md:text-[11px] uppercase font-medium mb-3"
        style={{ color: TEAL, letterSpacing: '0.2em' }}
      >
        {eyebrow}
      </p>
    )}
    {title && (
      <h2
        className="font-serif text-[22px] md:text-[26px] leading-[1.2] tracking-[-0.005em] mb-4"
        style={{ color: NAVY }}
      >
        {title}
      </h2>
    )}
    <div
      className="font-sans text-[15px] md:text-[16px] leading-[1.7] space-y-4"
      style={{ color: NAVY_MUTED }}
    >
      {children}
    </div>
  </section>
);

export const Callout = ({ children, testid }) => (
  <div
    className="max-w-[680px] mx-auto my-6 rounded-2xl p-5 md:p-6"
    style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
    data-testid={testid}
  >
    <div
      className="font-sans text-[14.5px] md:text-[15px] leading-[1.65]"
      style={{ color: NAVY }}
    >
      {children}
    </div>
  </div>
);

export const LegalPageShell = ({ eyebrow, title, subtitle, updated, children, current }) => (
  <div className="min-h-screen" style={{ backgroundColor: PAPER }}>
    {/* Top rail — minimal, paper, matches landing */}
    <header
      className="w-full"
      style={{ backgroundColor: PAPER, borderBottom: `1px solid ${HAIRLINE}` }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 md:py-6 flex items-center justify-between gap-6">
        <BackToHome />
        <TrustNav current={current} />
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-10 md:pb-16">
      <div className="max-w-[680px] mx-auto">
        {eyebrow && (
          <p
            className="font-sans text-[11px] md:text-[12px] uppercase font-medium mb-5 md:mb-6"
            style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}
            data-testid="legal-eyebrow"
          >
            {eyebrow}
          </p>
        )}
        <h1
          className="font-serif text-[32px] sm:text-[40px] md:text-[48px] leading-[1.08] tracking-[-0.01em]"
          style={{ color: NAVY }}
          data-testid="legal-title"
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className="font-sans mt-5 md:mt-7 text-[16px] md:text-[18px] leading-[1.55]"
            style={{ color: NAVY_MUTED }}
            data-testid="legal-subtitle"
          >
            {subtitle}
          </p>
        )}
        {updated && (
          <p
            className="font-sans mt-6 md:mt-8 text-[11.5px] md:text-[12px] uppercase"
            style={{ color: NAVY_SOFT, letterSpacing: '0.16em' }}
            data-testid="legal-updated"
          >
            Last updated · {updated}
          </p>
        )}
      </div>
    </section>

    <div className="h-px max-w-[680px] mx-auto" style={{ backgroundColor: HAIRLINE }} />

    {/* Body */}
    <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-12 md:pt-16 pb-16 md:pb-24">
      {children}
    </main>

    {/* Footer */}
    <footer
      style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div className="max-w-[680px] mx-auto flex flex-col md:flex-row items-start md:items-center gap-4 md:justify-between">
          <p
            className="font-sans text-[12.5px]"
            style={{ color: NAVY_SOFT }}
            data-testid="legal-footer-entity"
          >
            © 2026 Kindra AI Pty Ltd · Based in Brisbane, Queensland, Australia
          </p>
          <TrustNav current={current} />
        </div>
      </div>
    </footer>
  </div>
);

export default LegalPageShell;
